import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { FolderOpen, ChevronDown, ArrowUp, ChevronRight, Download } from "lucide-react";
import MaintenanceNotice from "../../Parts/MaintenceNotice";
import { components } from "react-select";
import Swal from "sweetalert2";

import AiraSelect from "../../Atoms/Select";
import SuggestionCard from "../../Atoms/SuggestionCard";
import UserService from "../../../services/User/UserService";
import PublishableService from "../../../services/Publishable/PublishableService";

import "../../../assets/css/global.css";
import "./styles.css";

const HomeTemplate = () => {
  const [userName, setUserName] = useState("");
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [userId, setUserId] = useState(null);
  const { keycloak } = useKeycloak();
  const [prompt, setPrompt] = useState("")
  
  const [selectedProject, setSelectedProject] = useState(null)
  const [showDeliverables, setShowDeliverables] = useState(false)
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const user = await UserService.getUserByToken(keycloak.token);
        setUserId(user.id);
        const publishables = await PublishableService.getPublishablesByUser(user.id, keycloak.token);
        const projectMap = publishables.reduce((map, pub) => {
          const { project, ...publishableData } = pub;
          if (!map.has(project.id)) {
            map.set(project.id, {
              id: project.id,
              name: project.name,
              prompt_suggestions_business: project.prompt_suggestions_business ?? [],
              publishables: []
            });
          }
          map.get(project.id).publishables.push(publishableData);
          return map;
        }, new Map());

        const groupedProjects = Array.from(projectMap.values());
        if (groupedProjects.length > 0) {
          setSelectedProject(groupedProjects[0]);
        }
        setUserProjects(groupedProjects);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setUserProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [keycloak]);

  
  const handleSubmit = (promptOverride) => {
    const text = typeof promptOverride === 'string' ? promptOverride : prompt;
    if (text.trim() !== ""){
      navigate(`/chat/${selectedProject.id}`, {
        state: {
          isGeral: true,
          prompt: text,
          projectName: selectedProject.name
        }
      })
    } else {
        Swal.fire({
          title: "Digite algo antes de enviar ao assistente!",
          customClass: {
            popup: 'custom-popup',
            title: 'custom-title',
            htmlContainer: 'custom-content',
            confirmButton: 'custom-confirm'
          }
        });
        return;
    }
  }

  const formatName = (fullName) => {
    if (!fullName) return "";
    const firstName = fullName.split(".")[0];
    const capitalizedFirstName =
      firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    return capitalizedFirstName;
  };

  const handleProjectSelection = (e) => {
    const projectId = parseInt(e.target.value);
    const fullProject = userProjects.find(p => p.id === projectId);
    if (fullProject) setSelectedProject(fullProject);
  };

  useEffect(() => {
    if (!keycloak.authenticated) {
      navigate("/login");
    } else {
      const username = keycloak?.tokenParsed?.given_name;
      setUserName(formatName(username));
    }

    showGlobalSystemMessage();
  }, [keycloak, navigate]);

  const showGlobalSystemMessage = (message) => {};

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && selectedProject) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const Control = ({ children, ...props }) => (
    <components.Control {...props}>
      <FolderOpen/> {children} <ChevronDown/>
    </components.Control>
  );

  const handleDownloadAllDeliverables = async () => {
    if (!userId) {
      return;
    }

    try {
      const zipBlob = await PublishableService.downloadPublishablesZipByUser(userId, keycloak.token, selectedProject.id);
      const url = window.URL.createObjectURL(new Blob([zipBlob]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedProject.name}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar entregáveis:", error);
      Swal.fire({
        title: "Erro ao baixar entregáveis",
        text: error.message || "Tente novamente mais tarde.",
        icon: "error",
        customClass: {
          popup: 'custom-popup',
          title: 'custom-title',
          htmlContainer: 'custom-content',
          confirmButton: 'custom-confirm'
        }
      });
    }
  };

  return (
    <div className="main overflow-auto">
      <MaintenanceNotice />
      <div className="breadcrumb justify-content-end">
          
      </div>

      <div className="main-section-container">
        <div className="d-flex justify-content-center flex-column main-section">
          <div className="welcome-section">
            <div>
              <h1>Olá {userName}, bem vindo!</h1>
              <h2>Comece a explorar e criar insights sobre o seu projeto!</h2>
            </div>

            <div className="select-aira-container">
              {loadingProjects ? (
                <div className="project-not-found-text">
                  <p>Carregando projetos...</p>
                </div>
              ) : userProjects.length > 0 ? (
                 <AiraSelect 
                    options={userProjects.map(project => ({ value: project.id, label: project.name })) || []}
                    defaultValue={userProjects.length > 0 ? { value: userProjects[0].id, label: userProjects[0].name } : null}
                    classNamePrefix="dark-select"
                    components={{ Control }}
                    onChange={handleProjectSelection}
                  />
              ) : (
                <div className="project-not-found-text">
                  <p>Nenhum projeto encontrado</p>
                </div>
              )}
            </div>
          </div>

          <div className="prompt-section">
            <div className="prompt-container">
              <div className="input-container">
                <input
                    type="text"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Pergunte qualquer coisa sobre o projeto"
                    className="prompt-input"
                />
                <div className="send-button-container">
                    <div className="send-button-box">
                        <button 
                          type="submit"
                          onClick={handleSubmit}
                          className="send-button"
                          style={{ cursor: !selectedProject ? 'not-allowed' : 'pointer' }}
                          disabled={!selectedProject}
                        >
                            <ArrowUp />
                        </button>                
                    </div>
                </div>
              </div>
            </div>
          </div>

          {selectedProject?.prompt_suggestions_business?.filter(s => s?.trim()) .length > 0 && (
            <div className="suggestions-container">
              {selectedProject.prompt_suggestions_business
                .filter(s => s?.trim())
                .map((suggestion, index) => (
                  <SuggestionCard
                    key={index}
                    text={suggestion}
                    onClick={() => {
                      setPrompt(suggestion);
                      handleSubmit(suggestion);
                    }}
                  />
                ))}
            </div>
          )}

          {selectedProject && (
          <div className="d-flex justify-content-center flex-column align-items-center">
            <div className={`deliverables-tags ${showDeliverables ? 'deliverables-tags--open' : ''}`}>
              {userProjects.find(p => p.id === selectedProject.id)?.publishables.map((pub) => (
                <a
                  key={pub.id}
                  href={pub.asset.signed_url}
                  download={pub.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="deliverable-btn"
                >
                  <Download size={14} />
                  <span>{pub.name}</span>
                </a>
              ))}
            </div>
            <button
              className="btn view-deliverables-btn"
              onClick={handleDownloadAllDeliverables}
              disabled={!userId}
            >
              Baixar todos entregáveis
            </button>
          </div>
          )}
        </div>
        <div className="aira-warning-container">
        <span className="aira-warning">Verifique sempre as informações geradas. A Aira pode cometer erros.</span>
      </div>
      </div>
    </div>
  );
};

export default HomeTemplate;
